import React, { useState } from "react";
import EmailForm from "./EmailForm";

const ServiceCard = ({
  title,
  description,
  features,
  timeline,
  borderColor,
  icon,
  onRequestConsultation,
}) => {
  return (
    <div
      className={`card p-6 flex flex-col text-center border-t-4 ${borderColor} shadow-lg`}
    >
      <div className="w-16 h-16 text-white mb-4 mx-auto">{icon}</div>
      <h3 className="text-2xl font-semibold text-white mb-3">{title}</h3>
      <p className="text-gray-400 mb-4">{description}</p>

      {/* Key Features */}
      <div className="flex-1 mb-6">
        <h4 className="text-sm font-semibold text-blue-400 mb-2">
          Key Features:
        </h4>
        <ul className="text-sm text-gray-300 space-y-1">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <span className="text-green-400 mr-2">✓</span>
              <span className="text-left">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Timeline */}
      <div className="mb-4">
        <div className="text-center">
          <div className="text-purple-400 font-semibold">Timeline</div>
          <div className="text-gray-300">{timeline}</div>
        </div>
      </div>

      <button
        onClick={() => onRequestConsultation(title)}
        className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition text-center"
      >
        Request Consultation &rarr;
      </button>
    </div>
  );
};

const ServiceCards = () => {
  const [showAll, setShowAll] = useState(false);
  const [isEmailFormOpen, setIsEmailFormOpen] = useState(false);
  const [selectedService, setSelectedService] = useState("");

  const handleRequestConsultation = (serviceTitle) => {
    setSelectedService(serviceTitle);
    setIsEmailFormOpen(true);
  };

  const closeEmailForm = () => {
    setIsEmailFormOpen(false);
    setSelectedService("");
  };

  const services = [
    {
      title: "Automation Framework Setup",
      description:
        "Complete automation framework implementation with modern tools and CI/CD integration",
      features: [
        "Framework architecture design",
        "TypeScript/Java setup with Playwright/Selenium",
        "Page Object Model implementation",
        "CI/CD pipeline integration",
        "Comprehensive reporting setup",
      ],
      timeline: "2-4 weeks",
      borderColor: "border-t-blue-500",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          className="w-full h-full"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
      ),
    },
    {
      title: "AI QA Agent Integration",
      description:
        "Revolutionary AI-powered test generation that creates Page Object Models and test suites automatically",
      features: [
        "AI-powered Page Object generation",
        "Intelligent test case creation",
        "Multi-framework support",
        "Natural language processing",
        "Continuous learning optimization",
      ],
      timeline: "1-2 weeks",
      borderColor: "border-t-green-500",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          className="w-full h-full"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      title: "Quality Strategy Consulting",
      description:
        "Comprehensive quality assessment and strategic roadmap for automation transformation",
      features: [
        "Current process gap analysis",
        "Quality metrics assessment",
        "Automation ROI analysis",
        "Strategic roadmap development",
        "Risk assessment & mitigation",
      ],
      timeline: "2-3 weeks",
      borderColor: "border-t-purple-500",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          className="w-full h-full"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
    },
    {
      title: "Team Training & Mentoring",
      description:
        "Hands-on training and mentoring to build internal QA expertise and sustainable practices",
      features: [
        "Test strategy training",
        "Automation design patterns",
        "Code review & best practices",
        "Quality metrics establishment",
        "Ongoing support programs",
      ],
      timeline: "3-6 months",
      borderColor: "border-t-orange-500",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          className="w-full h-full"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
          />
        </svg>
      ),
    },
    {
      title: "Legacy Framework Migration",
      description:
        "Modernize legacy Selenium frameworks to Playwright with minimal disruption and maximum efficiency",
      features: [
        "Legacy code assessment",
        "Modern framework migration",
        "Test suite optimization",
        "Performance improvements",
        "Team transition support",
      ],
      timeline: "4-8 weeks",
      borderColor: "border-t-red-500",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          className="w-full h-full"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      ),
    },
    {
      title: "CI/CD Integration Specialist",
      description:
        "Complete DevOps integration with automated testing pipelines and comprehensive reporting",
      features: [
        "GitHub Actions/Jenkins setup",
        "Docker containerization",
        "Parallel test execution",
        "Allure reporting integration",
        "Quality gates implementation",
      ],
      timeline: "2-3 weeks",
      borderColor: "border-t-yellow-500",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          className="w-full h-full"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      ),
    },
  ];

  const displayedServices = showAll ? services : services.slice(0, 4);

  return (
    <>
      <section id="services-cards" className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our <span className="text-gradient">Service Offerings</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Tailored automation consulting services designed to accelerate
              your QA maturity and deliver measurable business impact for
              growing engineering teams.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            {displayedServices.map((service, index) => (
              <ServiceCard
                key={index}
                title={service.title}
                description={service.description}
                features={service.features}
                timeline={service.timeline}
                borderColor={service.borderColor}
                icon={service.icon}
                onRequestConsultation={handleRequestConsultation}
              />
            ))}
          </div>

          {services.length > 4 && (
            <div className="text-center mt-12">
              <button
                onClick={() => setShowAll(!showAll)}
                className="px-8 py-3 bg-gray-800 text-white font-medium rounded-lg hover:bg-gray-700 transition"
              >
                {showAll ? "Show Less" : `View All ${services.length} Services`}
              </button>
            </div>
          )}

          {/* Value Proposition */}
          <div className="mt-16 bg-gradient-to-r from-gray-800/50 to-gray-900/50 rounded-2xl p-8 md:p-12 border border-gray-700/50 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Why Choose Our QA Consulting Services?
            </h3>
            <div className="grid md:grid-cols-3 gap-8 mt-8">
              <div>
                <div className="text-4xl mb-3">🎯</div>
                <h4 className="text-lg font-semibold text-white mb-2">
                  Proven Results
                </h4>
                <p className="text-gray-400 text-sm">
                  70% faster test development and 95% coverage improvement
                  across 50+ successful projects
                </p>
              </div>
              <div>
                <div className="text-4xl mb-3">🚀</div>
                <h4 className="text-lg font-semibold text-white mb-2">
                  AI-Powered Innovation
                </h4>
                <p className="text-gray-400 text-sm">
                  Proprietary AI QA agent that generates Page Object Models and
                  comprehensive test suites automatically
                </p>
              </div>
              <div>
                <div className="text-4xl mb-3">🛡️</div>
                <h4 className="text-lg font-semibold text-white mb-2">
                  Enterprise-Grade Quality
                </h4>
                <p className="text-gray-400 text-sm">
                  Modern, scalable solutions built for growth with comprehensive
                  mentoring and knowledge transfer
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Email Form Modal */}
      <EmailForm
        isOpen={isEmailFormOpen}
        onClose={closeEmailForm}
        selectedFramework={selectedService}
      />
    </>
  );
};

export default ServiceCards;
